<template>
    <!-- Only render the card when shifts are configured for the airports in
         view; otherwise it stays hidden (the empty stack item collapses). -->
    <widget-card v-if="loading || airports.length">
        <template #header>
            <Hourglass class="widget-icon" />
            <span class="widget-card-header__title">
                {{ trans('dashboard.v2.testing_compliance.by_shift.title') }}
            </span>
        </template>

        <div v-if="loading" class="flex flex-col gap-2">
            <SkeletonLoader v-for="i in 2" :key="i">
                <div class="h-12 w-full rounded-lg"></div>
            </SkeletonLoader>
        </div>

        <div v-else class="by-shift">
            <div v-for="airport in airports" :key="airport.id" class="by-shift__airport">
                <div v-if="airports.length > 1" class="by-shift__airport-name">{{ airport.name }}</div>
                <div class="by-shift__row">
                    <div v-for="shift in shiftRows(airport)" :key="shift.key" class="by-shift__cell">
                        <span class="by-shift__chip" :class="{ 'by-shift__chip--current': shift.current }">{{ shift.label }}</span>
                        <div class="by-shift__stat">
                            <span class="by-shift__value">{{ shift.required }}</span>
                            <span class="by-shift__label">{{ trans('dashboard.v2.testing_compliance.by_line.required') }}</span>
                        </div>
                        <div class="by-shift__stat">
                            <span class="by-shift__value by-shift__value--performed">{{ shift.performed }}</span>
                            <span class="by-shift__label">{{ trans('dashboard.v2.testing_compliance.by_line.performed') }}</span>
                        </div>
                        <div class="by-shift__stat">
                            <span class="by-shift__value by-shift__value--approved">{{ shift.approved }}</span>
                            <span class="by-shift__label">{{ trans('dashboard.v2.testing_compliance.by_line.approved') }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </widget-card>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import Hourglass from '@/../icons/Hourglass.vue';
import { eventBus, events } from '@/eventBus';
import { $http } from '@/bootstrap';
import { trans } from '@/mixins/i18n';
import { buildUrlWithQuery } from '@/Utils/buildUrlWithQuery';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

const dataUrl = computed(() => props.widgetData.dataUrl);

const loading = ref(true);
const airports = ref([]);

// Display-ready shift rows in chronological order, straight from the backend:
// { key, label (translated), current, required, performed, approved }. The
// current shift carries a `current` flag so the template fills its chip.
const shiftRows = (airport) => airport.shifts ?? [];

const fetchData = async (showLoading = false) => {
    try {
        loading.value = showLoading;
        const formData = props.filters?.form?.data?.() ?? {};
        const url = buildUrlWithQuery(dataUrl.value, { locations: formData.locations });
        const response = await $http.get(url);
        airports.value = response.data.airports ?? [];
    } catch (error) {
        airports.value = [];
        console.error('Failed to fetch shift compliance:', error);
    } finally {
        loading.value = false;
    }
};

watch(
    () => props.filters,
    () => fetchData(),
    { deep: true },
);

onMounted(() => {
    fetchData(true);
    eventBus.$on(events.dashboardWidgetRefresh, fetchData);
});

onBeforeUnmount(() => {
    eventBus.$off(events.dashboardWidgetRefresh, fetchData);
});
</script>

<style scoped>
.by-shift {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.by-shift__airport-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-body);
    margin-bottom: 0.5rem;
}

/* All shifts share the same panel-backed cell (chip + bold R·P·A tiles); only
   the chip differs — filled for the current shift, outlined for the rest. The
   cells spread evenly across the card on a grid, like the line/donut rows. */
.by-shift__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    /* Left-clustered (not space-evenly) so wrapped rows stay tidy on tablet /
       narrow widths instead of scattering cells to the edges. */
    justify-content: flex-start;
    gap: 0.625rem;
    width: 100%;
}

.by-shift__cell {
    display: flex;
    align-items: center;
    /* Content-sized grey panel; the row wraps cells to the next line when the
       card is too narrow to fit them side by side. Kept compact so a row of
       cells still fits on tablet / smaller widths. */
    gap: 0.625rem;
    padding: 0.3125rem 0.625rem;
    background: var(--color-panel);
    border-radius: 0.5rem;
}

.by-shift__chip {
    margin-right: -0.125rem;
    /* A shared min-width so every chip is the same size and the R·P·A tiles
       line up across cells when they stack on overflow. */
    min-width: 4rem;
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    color: var(--color-gray-text);
    background: transparent;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
}

.by-shift__chip--current {
    color: #fff;
    background: var(--color-body);
    border-color: var(--color-body);
}

.by-shift__stat {
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
}

.by-shift__value {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.1;
    color: var(--color-body);
    font-variant-numeric: tabular-nums;
}

.by-shift__value--performed {
    color: var(--color-compliance-pending);
}

.by-shift__value--approved {
    color: var(--color-compliance-approved);
}

.by-shift__label {
    font-size: 0.5625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: var(--color-gray-text);
}
</style>
